import { Link } from "react-router-dom";
import styled from "styled-components";

import { faCheck, faSort, faSortDown, faSortUp, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";

import { StatusBadge } from "pages/Sikring/components/StatusBadge";
import {
	FLYT_LABEL,
	FLYT_LABEL_SHORT,
	PRICE_UNIT,
	fmtDateOnly,
	fmtDateTime,
	formatMaalepunkter,
	formatNumber,
	fmtPrice,
	monthKeyOf,
	monthLabel,
	priceOf,
	scopeFull,
	scopeShort,
	totalVolumeKwh,
} from "pages/Sikring/sikringUtils";
import { Sikringsordre, Sikringsordrestatus } from "pages/Sikring/types";

/** Verdiene står i URL-en (?sorter=), så de må være stabile. */
export enum SortKey {
	Status = "status",
	Customer = "kunde",
	Created = "opprettet",
	Period = "periode",
	Volume = "volum",
	Price = "kundepris",
}

export enum SortDirection {
	Ascending = "stigende",
	Descending = "synkende",
}

const COLUMNS = "140px minmax(160px,220px) 88px 100px 110px 130px minmax(140px,1fr) 96px";
// Høyrestilte tall står ellers tett inntil venstrestilt tekst i neste kolonne.
const COLUMN_GAP = "16px";

interface SikringsordreTableProps {
	rows: Sikringsordre[];
	sortKey: SortKey;
	sortDirection: SortDirection;
	sortHref: (key: SortKey) => string;
	orderHref: (order: Sikringsordre) => string;
}

export function SikringsordreTable({ rows, sortKey, sortDirection, sortHref, orderHref }: Readonly<SikringsordreTableProps>) {
	const header = (key: SortKey, label: string) => {
		const isActive = key === sortKey;
		const ascending = sortDirection === SortDirection.Ascending;
		// Ingen ekte tabell, så aria-sort virker ikke - sorteringen står i lenketeksten i stedet.
		const description = isActive ? `${label}, sortert ${ascending ? "stigende" : "synkende"}` : `Sorter etter ${label.toLowerCase()}`;
		return (
			<div>
				<SortLink to={sortHref(key)} aria-label={description}>
					<span>{label}</span>
					<SortIcon icon={!isActive ? faSort : ascending ? faSortUp : faSortDown} $active={isActive} />
				</SortLink>
			</div>
		);
	};

	return (
		<Scroll>
			<TableInner>
				<HeaderRow>
					{header(SortKey.Status, "Status")}
					{header(SortKey.Customer, "Kunde")}
					{header(SortKey.Created, "Opprettet")}
					{header(SortKey.Period, "Periode")}
					{header(SortKey.Volume, "Volum (kWh)")}
					{header(SortKey.Price, "Kundepris")}
					<div>Omfang</div>
					<CenteredHeader>Kvalitetssikret</CenteredHeader>
				</HeaderRow>
				<Rows>
					{rows.map((o) => {
						const volume = totalVolumeKwh(o);
						return (
							<DataRow key={o.id}>
								<StatusCell>
									<StatusBadge status={o.status} short />
									{/* Sikringer bestilt før flyten kunne velges har ingen flyt. */}
									{o.sikring.flyt && <SecondaryText title={FLYT_LABEL[o.sikring.flyt]}>{FLYT_LABEL_SHORT[o.sikring.flyt]}</SecondaryText>}
								</StatusCell>
								<CustomerCell>
									<Truncate title={o.kunde?.navn ?? ""}>
										<RowLink to={orderHref(o)}>{o.kunde?.navn || "Ukjent kunde"}</RowLink>
									</Truncate>
									{o.kunde?.organisasjonsnummer && <SecondaryText>Org.nr. {o.kunde.organisasjonsnummer}</SecondaryText>}
								</CustomerCell>
								<MutedCell>{fmtDateOnly(o.opprettet)}</MutedCell>
								<MutedCell>
									{monthLabel(monthKeyOf(o.sikring.start))} –<br />
									{monthLabel(monthKeyOf(o.sikring.slutt))}
								</MutedCell>
								<NumberCell>
									{formatNumber(volume)}
									<br />
									<SecondaryText>
										{o.maanedsvolumer.length} {o.maanedsvolumer.length === 1 ? "måned" : "måneder"}
									</SecondaryText>
								</NumberCell>
								<NumberCell>
									{fmtPrice(priceOf(o.sikring))}
									<br />
									<SecondaryText>{PRICE_UNIT}</SecondaryText>
								</NumberCell>
								<ScopeCell>
									<Truncate title={scopeFull(o.sikring)}>{scopeShort(o.sikring)}</Truncate>
									{formatMaalepunkter(o.maalepunkter) && (
										<Truncate title={o.maalepunkter?.join(", ")}>
											<SecondaryText>{formatMaalepunkter(o.maalepunkter)}</SecondaryText>
										</Truncate>
									)}
								</ScopeCell>
								<QualityCheckCell order={o} />
							</DataRow>
						);
					})}
				</Rows>
			</TableInner>
			<Footnote>
				Prisene er eks. mva. Klikk på en kolonneoverskrift for å sortere, og på en rad for å se detaljer som feilårsak, månedsvolum og
				korrelasjons-IDene mot UtilityCloud.
			</Footnote>
		</Scroll>
	);
}

/**
 * Bare fullførte sikringsordrer kan kvalitetssikres, så de andre får en strek i stedet for et kryss.
 * Skjermlesere får teksten fra role="img" og aria-label på symbolet. title er bare et verktøytips for mus.
 */
function QualityCheckCell({ order }: Readonly<{ order: Sikringsordre }>) {
	if (order.kvalitetssikring) {
		const text = `Kvalitetssikret av ${order.kvalitetssikring.navn}, ${fmtDateTime(order.kvalitetssikring.tidspunkt)}`;
		return (
			<QualityCheckIcon title={text}>
				<FontAwesomeIcon icon={faCheck} color={colors.green700} role="img" aria-label={text} />
			</QualityCheckIcon>
		);
	}
	if (order.status !== Sikringsordrestatus.Completed)
		return (
			<QualityCheckIcon $muted>
				<span role="img" aria-label="Kan ikke kvalitetssikres ennå">
					–
				</span>
			</QualityCheckIcon>
		);
	return (
		<QualityCheckIcon title="Ikke kvalitetssikret">
			<FontAwesomeIcon icon={faXmark} color={colors.grey500} role="img" aria-label="Ikke kvalitetssikret" />
		</QualityCheckIcon>
	);
}

const QualityCheckIcon = styled.div<{ $muted?: boolean }>`
	align-self: center;
	text-align: center;
	font-size: 1rem;
	color: ${(props) => (props.$muted ? colors.grey800 : "inherit")};
`;

const CenteredHeader = styled.div`
	text-align: center;
`;

const Scroll = styled.div`
	overflow-x: auto;
`;

const TableInner = styled.div`
	/* Summen av kolonnenes minstebredder, gap og radens padding - ellers flyter siste kolonne ut av raden. */
	min-width: 1110px;
`;

// Uten min-width krymper ikke grid-cellene, og innholdet flyter inn i neste kolonne.
const HeaderRow = styled.div`
	display: grid;

	& > * {
		min-width: 0;
		overflow-wrap: anywhere;
	}

	grid-template-columns: ${COLUMNS};
	column-gap: ${COLUMN_GAP};
	row-gap: 8px;
	align-items: end;
	padding: 0 14px 8px;
	font-weight: 700;
	font-size: 0.75rem;
	line-height: 1.35;
	color: ${colors.grey800};
`;

const Rows = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const DataRow = styled.div`
	position: relative;
	display: grid;

	& > * {
		min-width: 0;
	}

	grid-template-columns: ${COLUMNS};
	column-gap: ${COLUMN_GAP};
	row-gap: 8px;
	align-items: start;
	padding: 10px 14px;
	background: ${colors.white};
	border-radius: 9px;
	box-shadow:
		inset 0 0 0 1px ${colors.grey300},
		0 2px 3px ${colors.grey900}4D;
	font-size: 0.8125rem;
	line-height: 1.35;

	&:hover {
		box-shadow:
			inset 0 0 0 1px ${colors.blue},
			0 2px 3px ${colors.grey900}4D;
	}
`;

// ::after dekker hele DataRow (nærmeste posisjonerte forelder), så hele raden er klikkbar med kundenavnet som lenketekst.
// Ingen celle mellom DataRow og lenken kan ha position, ellers krymper overlayet.
const RowLink = styled(Link)`
	color: inherit;
	text-decoration: none;

	&::after {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: 9px;
	}

	&:focus-visible::after {
		outline: 2px solid ${colors.blue};
	}
`;

// Pila krymper ikke, så den havner ikke oppå teksten når overskriften brytes.
const SortLink = styled(Link)`
	display: flex;
	align-items: center;
	gap: 6px;
	color: inherit;
	text-decoration: none;
`;

const SortIcon = styled(FontAwesomeIcon)<{ $active: boolean }>`
	flex-shrink: 0;
	opacity: ${(props) => (props.$active ? 1 : 0.4)};
`;

const NumberCell = styled.div`
	font-variant-numeric: tabular-nums;
`;

const ScopeCell = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

const MutedCell = styled.div`
	color: ${colors.grey800};
`;

const StatusCell = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 4px;
`;

const CustomerCell = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

const Truncate = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const SecondaryText = styled.span`
	color: ${colors.grey700};
	font-size: 0.75rem;
`;

const Footnote = styled.p`
	font-size: 0.75rem;
	color: ${colors.grey700};
	margin-top: 12px;
`;
